import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { getTicketCustomerServicesByCustomerID } from '@/modules/Ticket/services'
import {
    TicketCustomerService,
    TicketCustomerServiceInteresting,
} from '@/modules/Ticket/types/response'
import { SERVICE_CREATING_POC_KEY } from '@/modules/Ticket/constants'
import React, { useEffect, useMemo, useRef } from 'react'
import { Control, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormItem, Typogaphy } from 'vnetwork-ui'

export type TicketServiceOption =
    | (TicketCustomerService & {
          type: 'service'
          label: string
          value: number
      })
    | (TicketCustomerServiceInteresting & { type: 'service_interesting' })

interface Props {
    control: Control<any>
    onChangeService: (service: TicketServiceOption) => void
    customerID?: number
    journeyID?: number
    ticketTypeGroup?: string
    ticketTypeKey?: string
    setValue?: UseFormSetValue<any>
    autoSelectFirst?: boolean
}

const TicketService: React.FC<Props> = ({
    control,
    onChangeService,
    customerID,
    journeyID,
    ticketTypeGroup,
    ticketTypeKey,
    setValue,
    autoSelectFirst = false,
}) => {
    const { t } = useTranslation()
    const prevCustomerID = useRef<number | undefined>(undefined)
    const hasAutoSelected = useRef<boolean>(false)

    const {
        data: customerServices,
        renderState,
        isFetching,
    } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.TICKETS, customerID],
        queryFn: () =>
            getTicketCustomerServicesByCustomerID(
                t,
                customerID || 0,
                journeyID ? Number(journeyID) : undefined
            ),
        enabled: !!customerID,
    })

    // Filter options:
    // - Khi ticket_type_group là "service" thì chỉ lấy services, không lấy service_interestings
    // - Khi ticket_type_key là "service_creating_poc" thì chỉ lấy services có is_poc = true
    const filteredOptions = useMemo(() => {
        if (!customerServices) return []
        if (ticketTypeGroup === 'service') {
            let services = customerServices.filter(
                (item) => item.type === 'service'
            )
            // Filter POC services only for service_creating_poc
            if (ticketTypeKey === SERVICE_CREATING_POC_KEY) {
                services = services.filter(
                    (item) =>
                        item.type === 'service' &&
                        'is_poc' in item &&
                        item.is_poc === true
                )
            }
            return services
        }
        return customerServices
    }, [customerServices, ticketTypeGroup, ticketTypeKey])

    // Check if should show error: có customerID, đã load xong, nhưng không có options
    const showNoServiceError = useMemo(() => {
        return (
            !!customerID &&
            !isFetching &&
            customerServices !== undefined &&
            filteredOptions.length === 0
        )
    }, [customerID, isFetching, customerServices, filteredOptions])

    // Auto-select first service when customer changes or initial load with customerId
    useEffect(() => {
        // Reset flag when customer changes
        if (customerID !== prevCustomerID.current) {
            hasAutoSelected.current = false
            prevCustomerID.current = customerID
        }

        // Auto-select when conditions are met and haven't selected yet for this customer
        if (
            autoSelectFirst &&
            setValue &&
            customerID &&
            !hasAutoSelected.current &&
            !isFetching &&
            filteredOptions.length > 0
        ) {
            const firstService = filteredOptions[0] as TicketServiceOption
            setValue('service_id', firstService.value)
            onChangeService(firstService)
            hasAutoSelected.current = true
        }
    }, [
        autoSelectFirst,
        setValue,
        customerID,
        isFetching,
        filteredOptions,
        onChangeService,
    ])

    const stateComponent = renderState()
    if (stateComponent) return stateComponent
    return (
        <div>
            <FormItem
                control={control}
                name="service_id"
                type="select"
                disabled={!customerID || filteredOptions?.length === 0}
                label={t('ticket.services')}
                options={filteredOptions}
                returnFullOption
                onChangeAfterHookForm={(value) => {
                    onChangeService(value)
                }}
            />
            {showNoServiceError && (
                <Typogaphy size="small" className="text-red-500 mt-1">
                    {ticketTypeGroup === 'service'
                        ? t('ticket-service-no-active-service')
                        : t('ticket-service-no-service-found')}
                </Typogaphy>
            )}
        </div>
    )
}

export default TicketService
